/**
 * The locale the backend should resolve localized fields in, sent as `x-locale`
 * on every request.
 *
 * Server-side localization is real: the gateway resolves a `LocalizedText` down
 * to one language per request, and `x-locale` is what picks it. Without the
 * header the backend falls back to its canonical default, which is why the
 * catalog used to read Russian regardless of the UI language.
 *
 * The active language is a durable UI pref owned by `@noor/lib`'s `ui-store`,
 * but this package sits BELOW `@noor/lib` in the dependency graph and must not
 * import it. So the resolver is injected instead — exactly the shape
 * `auth-token.ts` uses for the bearer token, and wired once at app boot.
 */

/** Languages the platform speaks; mirrors `@noor/lib`'s `Language`. */
export type ApiLocale = 'ru' | 'uz' | 'en';

/** Russian is the backend's own canonical fallback for an untranslated field. */
const DEFAULT_LOCALE: ApiLocale = 'ru';

const LOCALES: readonly string[] = ['ru', 'uz', 'en'];

/**
 * How the client learns the active locale. Null until the app wires one, so a
 * consumer that never calls `setLocaleResolver` (tests, a headless script) just
 * gets the default rather than a crash.
 */
let resolveLocale: (() => string) | null = null;

/**
 * Point the client at the app's language state. Called once during boot with a
 * getter — not a value — so a language switch is picked up by the next request
 * without re-registering anything.
 */
export function setLocaleResolver(resolver: () => string): void {
  resolveLocale = resolver;
}

/**
 * The locale to send. Falls back to `ru` when nothing is wired or the resolver
 * answers with a language the API doesn't speak, so a bad value degrades to the
 * backend's own default instead of being sent verbatim and rejected.
 */
export function readLocale(): ApiLocale {
  const value = resolveLocale?.();
  return value !== undefined && LOCALES.includes(value)
    ? (value as ApiLocale)
    : DEFAULT_LOCALE;
}
